// Dettaglio in sola lettura degli elenchi (Clienti, Preventivi, Magazzino,
// Listini) [sc-48893]. Le funzioni costruiscono i gruppi che `DettaglioModal`
// mostra: { titolo, campi: [{ label, valore, link? }] } e, per le righe del
// preventivo, { titolo, tabella: { colonne, righe } }.
// I campi senza valore non compaiono, e nemmeno i gruppi rimasti vuoti.

import { eur } from "./pdfRiepilogo";
import { formatToLocalDate, formatToLocalDateAndHours } from "./formatting";
import { linkMaps } from "./indirizzo";
import { daRestituire } from "./pagamenti";
import { righeSpese } from "./spese";

const vuoto = (v) =>
  v === null || v === undefined || String(v).trim() === "" || v === "null";

const soldi = (v) =>
  vuoto(v) || !Number.isFinite(Number(v)) ? null : `€ ${eur(v)}`;

const numero = (v) =>
  vuoto(v) || !Number.isFinite(Number(v))
    ? null
    : String(Math.round(Number(v) * 100) / 100).replace(".", ",");

const indirizzo = (raw) => {
  if (vuoto(raw)) return null;
  if (typeof raw === "string") {
    try {
      return indirizzo(JSON.parse(raw));
    } catch {
      return { address: raw.trim() };
    }
  }
  const address = String(raw.address ?? "").trim();
  return address ? { address, link: raw.link } : null;
};

const campoIndirizzo = (label, raw) => {
  const loc = indirizzo(raw);
  return loc
    ? { label, valore: loc.address, link: loc.link || linkMaps(loc.address) }
    : { label, valore: null };
};

/** Toglie i campi vuoti e i gruppi senza contenuto. */
export function pulisciGruppi(gruppi) {
  return gruppi
    .map((g) =>
      g.tabella ? g : { ...g, campi: g.campi.filter((c) => !vuoto(c.valore)) }
    )
    .filter((g) => (g.tabella ? g.tabella.righe.length : g.campi.length));
}

export function dettaglioCliente(c) {
  if (!c) return [];
  return pulisciGruppi([
    {
      titolo: "Anagrafica",
      campi: [
        { label: "Cliente", valore: c.clientName },
        { label: "Ragione sociale", valore: c.companyName },
        { label: "Codice fiscale", valore: c.taxCode },
        { label: "Partita IVA", valore: c.vatNumber },
        { label: "Codice univoco", valore: c.uniqueCode },
      ],
    },
    {
      titolo: "Contatti",
      campi: [
        { label: "Telefono", valore: c.phoneNumber },
        { label: "Email", valore: c.email },
        { label: "PEC", valore: c.pec },
      ],
    },
    {
      titolo: "Indirizzi",
      campi: [
        campoIndirizzo("Sede legale", c.legalHeadquarters),
        campoIndirizzo("Residenza", c.residenceAddress),
        campoIndirizzo("Cantiere", c.worksiteAddress),
      ],
    },
    {
      titolo: "Preventivi",
      campi: [
        {
          label: "Ultimo preventivo",
          valore: vuoto(c.lastQuote)
            ? null
            : formatToLocalDateAndHours(c.lastQuote),
        },
        {
          label: "Ultimo preventivo confermato",
          valore: vuoto(c.lastEvent) ? null : formatToLocalDate(c.lastEvent),
        },
      ],
    },
  ]);
}

/** Righe del preventivo: articoli collegati con la loro riga (QuoteItem). */
function righePreventivo(q) {
  return (q.items || []).map((it) => {
    const qi = it.QuoteItem || {};
    const qta = Number(qi.quantity) || 0;
    const prezzo = Number(qi.price ?? it.price) || 0;
    return [
      it.itemCode || "—",
      it.itemName || it.brand || "—",
      numero(qta) ?? "0",
      vuoto(qi.iva) ? "—" : `${qi.iva}%`,
      soldi(prezzo * qta) ?? "—",
    ];
  });
}

export function dettaglioPreventivo(q) {
  if (!q) return [];
  const credito = daRestituire(q.realTotalPrice);
  return pulisciGruppi([
    {
      titolo: "Preventivo",
      campi: [
        { label: "Numero", valore: q.id },
        { label: "Etichetta", valore: q.label },
        {
          label: "Data",
          valore: vuoto(q.date) ? null : formatToLocalDate(q.date),
        },
        { label: "Cliente", valore: q.Client?.clientName },
        { label: "Telefono", valore: q.Client?.phoneNumber },
        { label: "Email", valore: q.Client?.email },
      ],
    },
    {
      titolo: "Stato",
      campi: [
        {
          label: "Conferma",
          valore: q.isConfirmed ? "Confermato" : "Da confermare",
        },
        { label: "Pagamento", valore: q.payment_state },
        { label: "Spedizione", valore: q.state },
      ],
    },
    {
      titolo: "Consegna",
      campi: [
        campoIndirizzo("Indirizzo di consegna", q.location),
        { label: "Metodo di pagamento", valore: q.payment_method },
        { label: "Note tecniche", valore: q.technical_queries },
      ],
    },
    {
      titolo: "Importi",
      campi: [
        { label: "Totale articoli", valore: soldi(q.totalPrice) },
        {
          label: "Sconto",
          valore: Number(q.discount) ? soldi(q.discount) : null,
        },
        {
          label: "Maggiorazione",
          valore: Number(q.surcharge) ? soldi(q.surcharge) : null,
        },
        // [sc-49299] spese aggiuntive presenti, senza IVA
        ...righeSpese(q).map((r) => ({
          label: r.label,
          valore: soldi(r.amount),
        })),
        {
          label: "Acconto",
          valore: Number(q.deposit) ? soldi(q.deposit) : null,
        },
        credito > 0
          ? { label: "Da restituire", valore: soldi(credito) }
          : { label: "Da pagare", valore: soldi(q.realTotalPrice) },
      ],
    },
    // [sc-49299] un versamento per riga; «non indicato» per i vecchi acconti
    {
      titolo: "Acconti",
      tabella: {
        colonne: ["Data", "Metodo", "Importo"],
        righe: [...(q.payments || [])]
          .sort((a, b) => new Date(a.paidAt) - new Date(b.paidAt))
          .map((p) => [
            vuoto(p.paidAt) ? "—" : formatToLocalDate(p.paidAt),
            p.method || "non indicato",
            soldi(p.amount) ?? "—",
          ]),
      },
    },
    {
      titolo: "Articoli",
      tabella: {
        colonne: ["Codice", "Descrizione", "Q.tà", "IVA", "Importo ivato"],
        righe: righePreventivo(q),
      },
    },
  ]);
}

export function dettaglioArticolo(i) {
  if (!i) return [];
  const w = i.warehouse || {};
  const misure =
    !vuoto(i.widthCm) && !vuoto(i.heightCm)
      ? `${numero(i.widthCm)} × ${numero(i.heightCm)} cm`
      : null;
  return pulisciGruppi([
    {
      titolo: "Articolo",
      campi: [
        { label: "Codice", valore: i.itemCode },
        { label: "Nome", valore: i.itemName },
        { label: "Brand", valore: i.brand },
        { label: "Sottomarca", valore: i.subBrand },
        { label: "Serie", valore: i.serie },
        { label: "Collezione", valore: i.collection },
        { label: "Categoria", valore: i.category },
        { label: "Sottocategoria", valore: i.subCategory },
        { label: "Tipo", valore: i.fixtureType },
        { label: "Posizione", valore: i.fixturePosition },
        { label: "Stato", valore: i.articleStatus },
      ],
    },
    {
      titolo: "Prezzi (IVA esclusa)",
      campi: [
        { label: "Prezzo listino", valore: soldi(i.price) },
        { label: "Prezzo scontato", valore: soldi(i.priceDiscounted) },
        // niente IVA: i prezzi di listino sono IVA esclusa e l'aliquota si
        // sceglie sulla riga del preventivo, non sull'articolo [sc-48892]
      ],
    },
    {
      titolo: "Caratteristiche",
      campi: [
        { label: "Formato", valore: i.formatLabel },
        { label: "Misure", valore: misure ?? i.measureDesc },
        {
          label: "Spessore",
          valore: vuoto(i.thicknessMm) ? null : `${numero(i.thicknessMm)} mm`,
        },
        {
          label: "Lunghezza",
          valore: vuoto(i.lengthMm) ? null : `${numero(i.lengthMm)} mm`,
        },
        { label: "Finitura", valore: i.finish },
        { label: "Superficie", valore: i.surface },
        { label: "Materiale", valore: i.material },
        { label: "Colore", valore: i.color },
        { label: "Unità di vendita", valore: i.salesUnit },
        { label: "Pezzi per scatola", valore: numero(i.piecesPerBox) },
        { label: "M² per scatola", valore: numero(i.sqmPerBox) },
        { label: "ML per scatola", valore: numero(i.mlPerBox) },
        { label: "Scatole per pallet", valore: numero(i.boxesPerPallet) },
        { label: "EAN", valore: i.ean },
        { label: "Note", valore: i.notes },
      ],
    },
    {
      titolo: "Magazzino",
      campi: [
        { label: "Giacenza", valore: numero(w.inWarehouse) },
        { label: "Scatole", valore: numero(w.boxQuantity) },
        { label: "M² totali", valore: numero(w.sqmTotal) },
        { label: "ML totali", valore: numero(w.mlTotal) },
      ],
    },
  ]);
}

/**
 * Ordine al fornitore. Stato e quantità arrivano già come testo dalla pagina
 * Ordini (`statusLabel`, `quantityLabel`), così le etichette restano una sola.
 */
export function dettaglioOrdine(o, { stato, quantita } = {}) {
  if (!o) return [];
  const it = o.item || {};
  const quando = (d) => (vuoto(d) ? null : formatToLocalDate(d));
  return pulisciGruppi([
    {
      titolo: "Ordine",
      campi: [
        { label: "Numero", valore: o.id },
        { label: "Stato", valore: stato ?? o.status },
        { label: "Quantità", valore: quantita ?? numero(o.quantity) },
        { label: "Quantità ordinata", valore: numero(o.quantity) },
        { label: "Confermati", valore: numero(o.confirmedQty) },
        { label: "Sospesi", valore: numero(o.suspendedQty) },
        {
          label: "Ricevuti",
          valore: Number(o.deliveredQty) ? numero(o.deliveredQty) : null,
        },
        {
          label: "Danneggiati",
          valore: Number(o.damagedQty) ? numero(o.damagedQty) : null,
        },
        { label: "Data ordine", valore: quando(o.orderedAt) },
        { label: "Completato il", valore: quando(o.completedAt) },
        {
          label: "Riordino dell'ordine",
          valore: vuoto(o.sourceOrderId) ? null : `#${o.sourceOrderId}`,
        },
        { label: "Note", valore: o.notes },
      ],
    },
    {
      titolo: "Articolo",
      campi: [
        { label: "Codice", valore: it.itemCode },
        { label: "Nome", valore: it.itemName },
        { label: "Brand", valore: it.brand },
        { label: "Categoria", valore: it.category },
      ],
    },
  ]);
}

// Elementi che fanno già qualcosa da soli: un click lì non apre il dettaglio
const INTERATTIVI =
  "button, a, input, select, textarea, label, summary, details, [role='button'], [role='menu'], [data-no-dettaglio]";

/**
 * Il click (o l'Invio) sulla riga apre il dettaglio? No se parte da un
 * controllo della riga (pulsanti, select, link) o se l'utente sta
 * selezionando del testo, per esempio per copiare un codice.
 */
export function clickApreDettaglio(
  event,
  selezione = typeof window !== "undefined" ? window.getSelection?.() : null
) {
  const target = event?.target;
  const riga = event?.currentTarget;
  // [sc-49300] il percorso del click resta valido anche se il controllo
  // sparisce nel frattempo (la tendina di StatusSelect si chiude da sola)
  const percorso = event?.composedPath?.() || [];
  const finoAllaRiga = riga ? percorso.slice(0, percorso.indexOf(riga)) : [];
  if (finoAllaRiga.some((el) => el.matches?.(INTERATTIVI))) return false;
  const interattivo = target?.closest?.(INTERATTIVI);
  if (
    interattivo &&
    interattivo !== riga &&
    (!riga || riga.contains(interattivo))
  ) {
    return false;
  }
  if (event?.type === "click" && selezione && String(selezione).trim())
    return false;
  return true;
}
